import { documentOf, windowOf } from '../anchor-document.util';

type TYPosition = 'start' | 'center-start' | 'center' | 'center-end' | 'end';
type TXPosition = 'start' | 'center' | 'end';
export type TTooltipConfig = {
    element: HTMLElement;
    title?: string;
    content?: string;
    multiples?: { title?: string; content?: string }[];
    xAxis?: TXPosition;
    yAxis?: TYPosition;
    hasDistance?: boolean;
    distance?: number;
}

export type TTooltipAnchorWatcher = {
    stop: () => void;
}

export type TTooltipAnchorHandlers = {
    /** A âncora deixou de estar à vista: o tooltip ficou órfão e tem de fechar. */
    onHidden: () => void;
    /** A âncora deslocou-se ou mudou de tamanho: basta reposicionar. */
    onMove: () => void;
}

/**
 * A âncora só continua a servir de âncora enquanto está ligada ao DOM, tem caixa
 * e cai dentro da viewport.
 *
 * O último critério é o que apanha o `cdkDrag`: ao iniciar o arrasto ele move o
 * elemento para o `body` com `position: fixed; left: -999em; opacity: 0` — e
 * mantém-no ligado de propósito, porque removê-lo interromperia a cadeia de
 * eventos no iOS. Sem este teste o tooltip acompanhava-o para fora do ecrã e
 * voltava com ele ao largar; como o `mouseleave` nunca chega a disparar durante
 * o arrasto, ficava preso por cima do item até o rato entrar e sair de novo.
 */
const isAnchorDisplayed = (element: HTMLElement, rect: DOMRect): boolean => {
    if (!element.isConnected) return false;
    if (rect.width === 0 && rect.height === 0) return false;
    const view = windowOf(element) ?? window;
    return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < view.innerHeight &&
        rect.left < view.innerWidth
    );
};

/**
 * O tooltip é anexado ao `body` com posição `fixed` calculada uma única vez, por
 * isso não acompanha a âncora: se ela sair do DOM ou mudar de sítio, o tooltip
 * fica órfão no ecrã. Os listeners de `mouseleave` também não resolvem — o
 * Firefox não dispara `mouseleave` quando o elemento sob o cursor é removido do
 * DOM (o Chrome dispara), que é exatamente o caso de uma view destruída com o
 * rato em cima.
 *
 * Este observador vigia a âncora enquanto o tooltip está visível: reposiciona-o
 * quando ela se mexe e só o invalida quando ela deixa de estar à vista. Fechar
 * em vez de seguir seria irrecuperável — uma âncora com `hover:scale`/`transition`
 * mexe-se por causa do próprio hover, e como o ponteiro nunca sai não haveria
 * novo `mouseenter` para reabrir.
 */
export const watchTooltipAnchor = (element: HTMLElement, handlers: TTooltipAnchorHandlers): TTooltipAnchorWatcher => {
    // Viewport e `requestAnimationFrame` da janela da âncora — ver `windowOf`.
    const view = windowOf(element) ?? window;
    let previous = element.getBoundingClientRect();
    let frameId: number | null = null;
    let stopped = false;

    const hasMoved = (rect: DOMRect): boolean =>
        Math.abs(rect.top - previous.top) > 0.5 ||
        Math.abs(rect.left - previous.left) > 0.5 ||
        Math.abs(rect.width - previous.width) > 0.5 ||
        Math.abs(rect.height - previous.height) > 0.5;

    const schedule = (): void => {
        if (stopped) return;
        try {
            // O Firefox lança NS_ERROR_NOT_AVAILABLE num documento já inativo;
            // nesse caso não há mais frames e o tooltip morre com a página.
            frameId = view.requestAnimationFrame(check);
        } catch {
            frameId = null;
        }
    };

    const check = (): void => {
        frameId = null;
        if (stopped) return;
        // Num elemento já desligado o rect vem todo a zero em vez de lançar, por
        // isso pode ser lido antes de saber se a âncora ainda serve.
        const rect = element.getBoundingClientRect();
        if (!isAnchorDisplayed(element, rect)) {
            handlers.onHidden();
            return;
        }
        if (hasMoved(rect)) {
            previous = rect;
            handlers.onMove();
        }
        schedule();
    };

    schedule();

    return {
        stop: (): void => {
            stopped = true;
            if (frameId !== null) view.cancelAnimationFrame(frameId);
            frameId = null;
        },
    };
}

export const createTooltip = (config: TTooltipConfig): HTMLDivElement | null => {
    config = {
        xAxis: 'start',
        yAxis: 'end',
        hasDistance: true,
        ...config
    }
    // O balão nasce e monta-se no documento da âncora — ver `documentOf`.
    const doc = documentOf(config.element) ?? document;
    const tooltip = doc.createElement('div');
    tooltip.classList.add(
        'z-[99999]',
        'fixed',
        'py-[4px]',
        'px-[10px]',
        'rounded-[6px]',
        'bg-tooltip',
        'max-w-[300px]',
        'shadow-sm',
        'border-border-inverted'
    );

    const contentContainer = doc.createElement('div');
    contentContainer.classList.add('relative');

    const createTitle = (title: string): HTMLHeadingElement => {
        const tooltipTitle = doc.createElement('h3');
        tooltipTitle.classList.add('label-semibold', 'text-tooltip-foreground', 'leading-[24px]', 'break-words');
        tooltipTitle.appendChild(doc.createTextNode(title));
        return tooltipTitle;
    }

    const createContent = (content: string): HTMLParagraphElement => {
        const tooltipContent = doc.createElement('p');
        tooltipContent.classList.add('content-sm-regular', 'text-foreground-75', 'leading-[20px]', 'break-words');
        tooltipContent.appendChild(doc.createTextNode(content));
        return tooltipContent;
    }

    if (config.title) {
        contentContainer.appendChild(createTitle(config.title));
    }

    if (config.content) {
        contentContainer.appendChild(createContent(config.content));
    }

    if (config.multiples?.length) {
        config.multiples.forEach((item, index) => {
            if (!item.title && !item.content) {
                return;
            }
            
            const itemWrapper = doc.createElement('div');
            
            const hasPreviousItems = index > 0 || config.title || config.content;
            if (hasPreviousItems) {
                itemWrapper.classList.add('pt-[8px]');
            }

            if (item.title) {
                itemWrapper.appendChild(createTitle(item.title));
            }
            if (item.content) {
                itemWrapper.appendChild(createContent(item.content));
            }
            contentContainer.appendChild(itemWrapper);
        });
    }

    tooltip.appendChild(contentContainer);
    // Documento a ser desmontado ou substituído: sem `body` não há onde montar.
    if (!doc.body) return null;
    doc.body.appendChild(tooltip);

    setTooltipPosition(tooltip, config);
    return tooltip;
}

export function setTooltipPosition(tooltip: HTMLDivElement, config: TTooltipConfig): void {

    const { width, height } = tooltip.getBoundingClientRect();
    const { innerWidth, innerHeight } = windowOf(config.element) ?? window;

    let left = calcLeftPosition(config.xAxis, config.yAxis, tooltip, config);
    let top = calcTopPosition(config.yAxis, tooltip, config);

    if (top + height > innerHeight) top = calcTopPosition('start', tooltip, config);
    if (top < 0) top = calcTopPosition('end', tooltip, config);

    if (left + width > innerWidth && config.xAxis === 'end') left = calcLeftPosition('start', config.yAxis, tooltip, config);
    if (left < 0 || left + width > innerWidth && config.xAxis === 'start') left = calcLeftPosition('end', config.yAxis, tooltip, config);

    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;
}

function calcTopPosition(position: TYPosition, tooltip: HTMLDivElement, config: TTooltipConfig): number {
    const { top, height } = config.element.getBoundingClientRect();
    const { height: elHeight } = tooltip.getBoundingClientRect();
    const defaultDistance = config.hasDistance ? 6 : 0;
    const distance = config.distance ?? defaultDistance;

    switch (position) {
        case 'start':
            return top - elHeight - distance;

        case 'center':
            return top + height / 2 - elHeight / 2;

        case 'end':
            return top + height + distance

        default:
            return top - elHeight - distance;
    }
}

function calcLeftPosition(positionX: TXPosition, positionY: TYPosition, tooltip: HTMLDivElement, config: TTooltipConfig): number {
    const { left, width } = config.element.getBoundingClientRect();
    const { width: elWidth } = tooltip.getBoundingClientRect();
    const defaultDistance = config.hasDistance ? 6 : 0;
    const distance = config.distance !== null ? config.distance : defaultDistance;

    switch (positionX) {
        case 'start':
            return positionY === 'center' ? left - elWidth - distance : left;
        case 'center':
            return left + width / 2 - elWidth / 2;

        case 'end':
            return positionY === 'center' ? left + width + distance : left + width - elWidth;

        default:
            return positionY === 'center' ? left - elWidth - distance : left;
    }
}